import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import useUserRoles from "hooks/useUserRoles";

import NuevaDeudaForm from "pages/procesos/registrar/components/DeudasForm/components/NuevaDeudaForm/index";
import DatosBasicosApi from "api/datosbasicosApi";
import ProcesosApi from "api/procesosApi";

const TabAcreencias = ({ proceso }) => {
  const { user, roles } = useUserRoles();
  const [deudas, setDeudas] = useState({});
  const [clasesDeuda, setClasesDeuda] = useState([]);
  const [showNewDeudaModal, setShowNewDeudaModal] = useState(false);

  const fetchDatosIniciales = async () => {
    try {
      const [respDeudas, respClasesDeudas] = await Promise.all([
        ProcesosApi.obtenerDeudasProceso(proceso.id),
        DatosBasicosApi.fetchClasesDeuda(),
      ]);
      setClasesDeuda(respClasesDeudas.data.sort((a, b) => a.id - b.id));
      //alert(JSON.stringify(respDeudas.data));
      const deudasFormateadas = respDeudas.data.deudas.map((deuda) => {
        const { formatted: capiFormatted, value: capiValue } = formatCurrency(
          String(deuda.capital || 0)
        );
        const { formatted: interesesFormatted, value: interesesValue } = formatCurrency(
          String(deuda.intereses || 0)
        );
        const { formatted: inteMoraFormatted, value: inteMoraValue } = formatCurrency(
          String(deuda.intereses_mora || 0)
        );
        const { formatted: otrosFormatted, value: otrosValue } = formatCurrency(
          String(deuda.otros_valores || 0)
        );
        const { formatted, value } = formatCurrency(String(deuda.capital_conciliado || 0));
        return {
          ...deuda,
          capital: capiValue,
          capital_formatted: capiFormatted,
          intereses: interesesValue,
          intereses_formatted: interesesFormatted,
          intereses_mora: inteMoraValue,
          intereses_mora_formatted: inteMoraFormatted,
          otros_valores: otrosValue,
          otros_valores_formatted: otrosFormatted,
          capital_conciliado: value,
          capital_conciliado_formatted: formatted,
        };
      });
      setDeudas(deudasFormateadas);
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
    }
  };

  useEffect(() => {
    if (proceso) {
      fetchDatosIniciales();
    }
  }, [user, proceso]);

  const formatCurrency = (value) => {
    const number = value ? value.replace(/\D/g, "") : "0";
    const numberValue = Number(number);
    const formatter = new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return {
      formatted: formatter.format(number || 0).replace(/\s/g, ""),
      value: numberValue,
    };
  };

  const handleChange = (field, deudaId) => (event) => {
    const value = event.target.value;
    const index = deudas.findIndex((deuda) => deuda.id === deudaId);
    const nuevasDeudas = [...deudas];
    if (index !== -1) {
      if (field === "etiqueta") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          etiqueta: value,
        };
      }
    }
    setDeudas(nuevasDeudas);
  };

  const handleMoneyChange = (field, deudaId) => (event) => {
    const { formatted, value } = formatCurrency(event.target.value);
    const index = deudas.findIndex((deuda) => deuda.id === deudaId);
    const nuevasDeudas = [...deudas];
    if (index !== -1) {
      if (field === "capital") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          capital: value,
          capital_formatted: formatted,
        };
      }
      if (field === "capital_conciliado") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          capital_conciliado: value,
          capital_conciliado_formatted: formatted,
        };
      }
      if (field === "intereses") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          intereses: value,
          intereses_formatted: formatted,
        };
      }
      if (field === "intereses_mora") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          intereses_mora: value,
          intereses_mora_formatted: formatted,
        };
      }
      if (field === "otros_valores") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          otros_valores: value,
          otros_valores_formatted: formatted,
        };
      }
    }
    setDeudas(nuevasDeudas);
  };

  const handleNewDeudaSubmit = async (deudaData) => {
    try {
      // Obtener las deudas existentes de la misma clase
      const deudasMismaClase = deudas.filter(
        (d) => d.clase_deuda === Number(deudaData.clase_deuda)
      );
      // Calcular el siguiente orden
      const ultimoOrden =
        deudasMismaClase.length > 0 ? Math.max(...deudasMismaClase.map((d) => d.orden ?? 0)) : -1;
      // Añadir el orden a los datos de la deuda
      const deudaConOrden = {
        ...deudaData,
        orden: ultimoOrden + 1,
      };
      await ProcesosApi.crearDeudas(proceso.id, deudaConOrden);
      await fetchDatosIniciales();
      setShowNewDeudaModal(false);
    } catch (error) {
      console.error("Error al crear deuda:", error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await Promise.all(
      deudas.map(async (deuda) => {
        await ProcesosApi.actualizarDeuda(proceso.id, deuda.id, deuda);
      })
    );
    alert("Acreencias actualizadas exitosamente.");
  };

  const deudasArray = Array.isArray(deudas) ? deudas : [];
  const deudasAgrupadasPorClase = deudasArray.reduce((acc, deuda) => {
    const clase = deuda.str_clase_deuda.nombre || "Sin Clase";
    if (!acc[clase]) {
      acc[clase] = {
        deudas: [],
        totalCapital: 0,
        totalCapitalConciliado: 0,
      };
    }
    acc[clase].deudas.push(deuda);
    acc[clase].totalCapital += deuda.capital || 0;
    acc[clase].totalCapitalConciliado += deuda.capital_conciliado || 0;
    return acc;
  }, {});

  const totalGeneralCapital = deudasArray.reduce((sum, deuda) => sum + (deuda.capital || 0), 0);
  // const totalGeneralCapitalConciliado = deudasArray.reduce(
  //   (sum, deuda) => sum + (deuda.capital_conciliado || 0),
  //   0
  // );

  return (
    <Box sx={{ mt: 2 }}>
      <form onSubmit={handleSave}>
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={6} sx={{ mt: 1, display: "flex", justifyContent: "flex-start", gap: 2 }}>
            <SuiButton
              variant="gradient"
              buttonColor="info"
              onClick={() => setShowNewDeudaModal(true)}
            >
              Agregar Deuda
            </SuiButton>
            <NuevaDeudaForm
              open={showNewDeudaModal}
              onClose={() => setShowNewDeudaModal(false)}
              onSubmit={handleNewDeudaSubmit}
              procesoId={proceso?.id}
              clasesDeuda={clasesDeuda}
            />
          </Grid>
        </Grid>
        {Object.entries(deudasAgrupadasPorClase).map(([clase, data]) => {
          // const porcConciliadoSobreCapitalConciliado =
          //   totalGeneralCapitalConciliado > 0
          //     ? ((data.totalCapitalConciliado / totalGeneralCapitalConciliado) * 100).toFixed(2)
          //     : "0.00";
          const porcCapitalSobreCapitalTotal =
            totalGeneralCapital > 0
              ? ((data.totalCapital / totalGeneralCapital) * 100).toFixed(2)
              : "0.00";
          return (
            <Box key={clase} sx={{ mt: 1 }}>
              <SuiTypography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                {clase}
              </SuiTypography>
              <Grid container spacing={1}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Acreedor
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Etiqueta
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#eb984e" }}>Acreencia</span>
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#eb984e" }}>Coeficiente</span>
                        </TableCell>
                        {/* <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#1976d2" }}>Conciliado</span>
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#1976d2" }}>Derecho</span>
                        </TableCell> */}
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Intereses
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Int. Mora
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Otros Val.
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Total
                        </TableCell>
                      </TableRow>
                      {data.deudas.map((deuda) => {
                        // Calcular la suma total para cada fila
                        const sumaTotal =
                          (deuda.capital_conciliado || 0) +
                          (deuda.intereses || 0) +
                          (deuda.intereses_mora || 0) +
                          (deuda.otros_valores || 0);
                        // Formatear la suma total
                        const { formatted: sumaTotalFormatted } = formatCurrency(String(sumaTotal));
                        return (
                          <TableRow key={deuda.id}>
                            <TableCell sx={{ padding: "2px 2px" }}>
                              {deuda.str_acreedor.nombre}
                            </TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>
                              <SuiInput
                                value={deuda.etiqueta}
                                onChange={handleChange("etiqueta", deuda.id)}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                              <SuiInput
                                value={deuda.capital_formatted}
                                onChange={handleMoneyChange("capital", deuda.id)}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                              <span style={{ color: "#eb984e", fontWeight: "bold" }}>
                                {((deuda.capital / totalGeneralCapital) * 100).toFixed(2)} %
                              </span>
                            </TableCell>
                            {/* <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                              <SuiInput
                                value={deuda.capital_conciliado_formatted}
                                onChange={handleMoneyChange("capital_conciliado", deuda.id)}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                              <span style={{ color: "#1976d2", fontWeight: "bold" }}>
                                {(
                                  (deuda.capital_conciliado / totalGeneralCapitalConciliado) *
                                  100
                                ).toFixed(2)}{" "}
                                %
                              </span>
                            </TableCell> */}
                            <TableCell sx={{ padding: "2px 2px" }}>
                              <SuiInput
                                value={deuda.intereses_formatted}
                                onChange={handleMoneyChange("intereses", deuda.id)}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>
                              <SuiInput
                                value={deuda.intereses_mora_formatted}
                                onChange={handleMoneyChange("intereses_mora", deuda.id)}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell sx={{ padding: "2px 2px" }}>
                              <SuiInput
                                value={deuda.otros_valores_formatted}
                                onChange={handleMoneyChange("otros_valores", deuda.id)}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                              {sumaTotalFormatted}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                        <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                        <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold",
                            padding: "2px 2px",
                            color: "#eb984e",
                          }}
                        >
                          {formatCurrency(String(data.totalCapital)).formatted}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold",
                            color: "#eb984e",
                            padding: "2px 2px",
                          }}
                        >
                          {porcCapitalSobreCapitalTotal} %
                        </TableCell>
                        {/* <TableCell
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold",
                            padding: "2px 2px",
                            color: "#1976d2",
                          }}
                        >
                          {formatCurrency(String(data.totalCapitalConciliado)).formatted}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold",
                            color: "#1976d2",
                            padding: "2px 2px",
                          }}
                        >
                          {porcConciliadoSobreCapitalConciliado} %
                        </TableCell> */}
                        <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                        <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                        <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                        <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                        <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                        <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Divider sx={{ borderColor: "gray", borderStyle: "groove" }} />
            </Box>
          );
        })}
        {(roles.isAdmin || roles.isCoordinador || roles.isOperador || roles.isDirector) && (
          <SuiButton type="submit" variant="gradient" buttonColor="dark">
            {proceso ? "Actualizar Acreencias" : "..."}
          </SuiButton>
        )}
      </form>
    </Box>
  );
};

TabAcreencias.propTypes = {
  proceso: PropTypes.object,
};

export default TabAcreencias;
